import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { LoadingPage } from '../ui/Loading';
import { Permission } from '../../config/permissions';
import { getFirstAccessibleRoute } from '../../config/routes';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: Permission;
  requiredRole?: string | string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  requiredRole,
}) => {
  const { isAuthenticated, isLoading, hasPermission, hasRole, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingPage message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role permission
  if (requiredRole && !hasRole(requiredRole)) {
    const isSuperAdmin = user?.role?.toLowerCase() === 'super_admin';
    const redirectPath = getFirstAccessibleRoute(hasPermission, isSuperAdmin);
    return <Navigate to={redirectPath} replace />;
  }

  // Check specific permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    // If trying to access dashboard without permission, redirect to first accessible route
    if (location.pathname === '/dashboard' || location.pathname === '/') {
      const isSuperAdmin = user?.role?.toLowerCase() === 'super_admin';
      const redirectPath = getFirstAccessibleRoute(hasPermission, isSuperAdmin);
      return <Navigate to={redirectPath} replace />;
    }
    // Otherwise, show forbidden page
    return (
      <Navigate
        to="/forbidden"
        state={{ from: location.pathname, missingPermission: requiredPermission, role: user?.role }}
        replace
      />
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
